import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { snowflakeApi } from '../utils/api';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  TextField, 
  Box, 
  Alert, 
  Chip,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Stack,
  Divider
} from '@mui/material';
import { 
  SkipNext as SkipIcon, 
  Category as TopicIcon,
  List as KeywordIcon 
} from '@mui/icons-material';

interface SkipToModule3Props {
  workflowId: string;
  clientId: string;
}

interface SkipResponse {
  success: boolean;
  workflow_id: string;
  topic_framework_blob: string;
  relevant_keywords_blob: string;
  original_keywords_blob: string;
  message: string;
  next_steps: string[];
}

const SkipToModule3: React.FC<SkipToModule3Props> = ({
  workflowId,
  clientId
}) => {
  const navigate = useNavigate();
  const { updateWorkflowData } = useApp();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SkipResponse | null>(null);
  const [suggestedCategories, setSuggestedCategories] = useState<string>('');
  const [forceOverwrite, setForceOverwrite] = useState(false);

  const handleSkipToModule3 = async () => {
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const requestBody = {
        workflow_id: workflowId,
        client_id: clientId,
        suggested_categories: suggestedCategories 
          ? suggestedCategories.split(',').map(cat => cat.trim()).filter(cat => cat.length > 0)
          : null,
        force_overwrite: forceOverwrite
      };

      const { data: result } = await snowflakeApi.post<SkipResponse>(
        '/api/v1/skip/to-module3',
        requestBody
      );
      setSuccess(result);

      // Update workflow data with the generated files
      updateWorkflowData({
        topicFrameworkBlob: result.topic_framework_blob,
        relevantKeywordsBlob: result.relevant_keywords_blob,
        skippedModule2: true
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProceedToModule3 = () => {
    if (success) {
      // Navigate to Module 3 using the correct route format
      navigate(`/module3/client/${clientId}/${workflowId}`);
    }
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', my: 3 }}>
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={2}>
            <SkipIcon color="primary" fontSize="large" />
            <Typography variant="h5" component="h2" color="primary">
              Skip to Module 3
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary">
            Bypass Module 2's heavy processing (which can take hours for large keyword sets) and create an 
            intelligent topical framework to proceed directly to Module 3. This feature attempts to use your 
            Module 1 analysis results for smart categorization, or falls back to proven default categories.
          </Typography>

          <Divider />

          {/* Configuration Options */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Configuration Options
            </Typography>
            
            <Stack spacing={2}>
              <TextField
                label="Custom Categories (optional)"
                placeholder="Brand Research, Location Services, Price Comparison, Technical Support"
                value={suggestedCategories}
                onChange={(e) => setSuggestedCategories(e.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText="Enter comma-separated category names to override the default framework. If left empty, the system will use your Module 1 analysis results or 8 proven default categories."
                disabled={isProcessing}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceOverwrite}
                    onChange={(e) => setForceOverwrite(e.target.checked)}
                    disabled={isProcessing}
                  />
                }
                label="Force overwrite existing framework"
              />
            </Stack>
          </Box>

          <Divider />

          {/* Current Configuration */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Current Configuration
            </Typography>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip icon={<KeywordIcon />} label={`Workflow: ${workflowId}`} size="small" />
                <Chip label={`Client: ${clientId}`} size="small" />
              </Box>
            </Stack>
          </Box>

          {/* Processing Status */}
          {isProcessing && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Creating minimal framework...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Error:</strong> {error}
              </Typography>
            </Alert>
          )}

          {/* Success Display */}
          {success && (
            <Alert severity="success">
              <Stack spacing={2}>
                <Typography variant="body2">
                  <strong>Success!</strong> {success.message}
                </Typography>
                
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Generated Files:
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TopicIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {success.topic_framework_blob}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <KeywordIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {success.relevant_keywords_blob}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Alert>
          )}

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            
            {!success ? (
              <Button
                variant="contained"
                startIcon={<SkipIcon />}
                onClick={handleSkipToModule3}
                disabled={isProcessing || !workflowId || !clientId}
              >
                {isProcessing ? 'Processing...' : 'Skip to Module 3'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={handleProceedToModule3}
              >
                Proceed to Module 3
              </Button>
            )}
          </Box>

          {/* Information Box */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>How this works:</strong>
              <br />
              • <strong>Intelligent Categorization:</strong> First attempts to use your Module 1 analysis results for topic-based categorization
              <br />
              • <strong>Fallback Categories:</strong> If Module 1 results unavailable, uses 8 proven categories: Information Seeking, Problem Resolution, Comparison Shopping, Location Specific, Brand Specific, Cost Related, Process Instructions, General Inquiries
              <br />
              • <strong>Original Keywords:</strong> Uses your full uploaded keyword set (no filtering)
              <br />
              • <strong>Fast Processing:</strong> Completes in seconds instead of hours for large datasets
              <br />
              • <strong>Module 3 Ready:</strong> Generates all files in the exact format expected by Module 3
              <br />
              <br />
              <strong>Best for:</strong> Large keyword sets (10k+ keywords), time-sensitive projects, or when Module 2's detailed classification isn't needed for your use case.
            </Typography>
          </Alert>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SkipToModule3; 